import * as React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Input } from './input';
import './PasswordInput.css';

/**
 * Password input with a built-in show/hide toggle (eye icon).
 *
 * Always renders the styled `Input` (`.lnr-field`); a supplied `className`
 * is appended so page classes (`form-input`, `gs-input`) still apply on top
 * of the shared field recipe. The toggle is a 44px round hit target pinned
 * inside the field's right edge (`.lnr-password__toggle`), drawn with lucide
 * Eye / EyeOff on the `lnr-icon` class so it stays stroke-only in dark mode.
 * All positioning lives in PasswordInput.css, so pages no longer need
 * `!important` overrides to reshape it.
 *
 * Same props and behaviour as before: every input prop is forwarded, the
 * toggle is `type="button"` and out of the tab order (tabIndex -1).
 */
export function PasswordInput({
  className = '',
  style,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement>) {
  const [revealed, setRevealed] = React.useState(false);
  const label = revealed ? 'Hide password' : 'Show password';

  return (
    <div className="lnr-password">
      <Input
        {...props}
        className={`lnr-password__input ${className}`.trim()}
        type={revealed ? 'text' : 'password'}
        style={style}
      />
      <button
        type="button"
        className="lnr-password__toggle"
        onClick={() => setRevealed((r) => !r)}
        aria-label={label}
        aria-pressed={revealed}
        title={label}
        tabIndex={-1}
      >
        {revealed ? (
          <EyeOff size={18} strokeWidth={1.75} className="lnr-icon" aria-hidden="true" />
        ) : (
          <Eye size={18} strokeWidth={1.75} className="lnr-icon" aria-hidden="true" />
        )}
      </button>
    </div>
  );
}
